import React, { useCallback, useRef, useState } from 'react';
import { X, Pin, FileCode, FileText } from 'lucide-react';
import { useScriptEditorStore } from '../../store/scriptEditorStore';
import { cn } from '../../utils/cn';

export const ScriptEditorTabs: React.FC = () => {
  const {
    files,
    openTabs,
    activeTabId,
    setActiveTab,
    closeFile,
    pinTab,
    unpinTab,
    reorderTabs,
  } = useScriptEditorStore();

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  const handleTabClick = useCallback((fileId: string) => {
    setActiveTab(fileId);
  }, [setActiveTab]);

  const handleTabClose = useCallback((e: React.MouseEvent, fileId: string) => {
    e.stopPropagation();
    closeFile(fileId);
  }, [closeFile]);

  const handleTabDoubleClick = useCallback((e: React.MouseEvent, fileId: string, isPinned: boolean) => {
    e.stopPropagation();
    if (isPinned) {
      unpinTab(fileId);
    } else {
      pinTab(fileId);
    }
  }, [pinTab, unpinTab]);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    if (fromIndex !== null && fromIndex !== toIndex) {
      reorderTabs(fromIndex, toIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, reorderTabs]);

  const getFileIcon = (language: 'typescript' | 'javascript') => {
    return language === 'typescript' ? (
      <FileCode className="w-4 h-4 text-blue-400 flex-shrink-0" />
    ) : (
      <FileText className="w-4 h-4 text-yellow-400 flex-shrink-0" />
    );
  };

  if (openTabs.length === 0) {
    return null;
  }

  return (
    <div
      ref={tabsRef}
      className="flex items-center bg-zinc-900 border-b border-zinc-800 overflow-x-auto scrollbar-thin scrollbar-thumb-zinc-700"
    >
      {openTabs.map((tab, index) => {
        const file = files[tab.fileId];
        if (!file) return null;

        const isActive = activeTabId === tab.fileId;
        const isDragging = draggedIndex === index;
        const isDragOver = dragOverIndex === index;

        return (
          <div
            key={tab.fileId}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, index)}
            onClick={() => handleTabClick(tab.fileId)}
            onDoubleClick={(e) => handleTabDoubleClick(e, tab.fileId, tab.isPinned)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm border-r border-zinc-800 cursor-pointer transition-colors min-w-0',
              'hover:bg-zinc-800',
              isActive ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400',
              isDragging && 'opacity-50',
              isDragOver && 'border-l-2 border-l-blue-500',
              tab.isPreview && 'italic'
            )}
            title={file.path}
          >
            {getFileIcon(file.language)}

            <span className="truncate max-w-[120px]">
              {file.name}
            </span>

            {file.isDirty && (
              <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" title="Unsaved changes" />
            )}

            {tab.isPinned ? (
              <Pin className="w-3 h-3 text-zinc-500 flex-shrink-0" />
            ) : (
              <button
                onClick={(e) => handleTabClose(e, tab.fileId)}
                className="p-0.5 rounded hover:bg-zinc-700 text-zinc-500 hover:text-zinc-300 flex-shrink-0"
                title="Close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
